import { useEffect, useState } from 'react'
import {
  CheckIcon,
  Mtds,
  ScreenHeader,
  AppLayout,
  useAvdeling,
  useDefaultSettings,
  useGetAvdelingerQuery,
  useGetRegionerQuery,
  useGetSeksjonerQuery,
  useGoBack,
  useGetTeamQuery
} from 'prove-common'

type SelectDivisionScreenProps = {
  topBar?: React.ReactNode
  sidebar?: React.ReactNode
  useMobileBar?: boolean
}

const fromIdNavn = (arr?: { id: string; navn?: string; name?: string }[]) =>
  arr?.map(({ id, navn, name }) => ({
    label: navn ?? name ?? '',
    value: String(id)
  })) ?? []

type DivisionOption = ReturnType<typeof fromIdNavn>[number]

type DivisionSuggestionFieldProps = {
  label: string
  placeholder: string
  options: DivisionOption[]
  selectedValue?: string
  onSelectedChange: (value?: string) => void
}

const DivisionSuggestionField = ({
  label,
  placeholder,
  options,
  selectedValue,
  onSelectedChange
}: DivisionSuggestionFieldProps) => (
  <Mtds.Field label={label}>
    <Mtds.Field.Suggestion
      selected={options.filter(({ value }) => value === selectedValue)}
      onSelectedChange={([item]) => onSelectedChange(item?.value)}
    >
      <Mtds.Input placeholder={placeholder} />
      <button type='reset' aria-label='Fjern tekst' />
      <Mtds.Field.Datalist>
        {options.map(({ label, value }) => (
          <Mtds.Field.Option key={value} value={value}>
            {label}
          </Mtds.Field.Option>
        ))}
      </Mtds.Field.Datalist>
    </Mtds.Field.Suggestion>
  </Mtds.Field>
)

export const SelectDivisionScreen = ({
  topBar,
  sidebar,
  useMobileBar
}: SelectDivisionScreenProps) => {
  const goBack = useGoBack('/settings')
  const defaultSettings = useDefaultSettings(true)
  const settings = useAvdeling()
  const { get, set } = settings
  const selectedRegionId = get?.selectedRegionId
  const selectedAvdelingId = get?.selectedAvdelingId
  const selectedSeksjonId = get?.selectedSeksjonId
  const selectedTeamId = get?.selectedTeamId
  const [region, setRegion] = useState(selectedRegionId)
  const [avdeling, setAvdeling] = useState(selectedAvdelingId)
  const [seksjon, setSeksjon] = useState(selectedSeksjonId)
  const [team, setTeam] = useState(selectedTeamId)
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  useEffect(() => {
    if (hasLocalChanges) return

    setRegion(selectedRegionId)
    setAvdeling(selectedAvdelingId)
    setSeksjon(selectedSeksjonId)
    setTeam(selectedTeamId)
  }, [
    selectedRegionId,
    selectedAvdelingId,
    selectedSeksjonId,
    selectedTeamId,
    hasLocalChanges
  ])

  const {
    data: regioner,
    isPending: isPendingRegioner,
    isError: isErrorRegioner,
    error: regionerError
  } = useGetRegionerQuery()

  const {
    data: avdelinger,
    isError: isErrorAvdelinger,
    error: avdelingerError
  } = useGetAvdelingerQuery(region)
  const {
    data: seksjoner,
    isError: isErrorSeksjoner,
    error: seksjonerError
  } = useGetSeksjonerQuery(avdeling)
  const {
    data: teams,
    isError: isErrorTeams,
    error: teamsError
  } = useGetTeamQuery(seksjon, avdeling)

  const regionOptions = fromIdNavn(regioner)
  const avdelingOptions = fromIdNavn(avdelinger)
  const seksjonOptions = fromIdNavn(seksjoner)
  const teamOptions = fromIdNavn(teams)

  const isLoading = settings.isPending || isPendingRegioner
  const isError =
    settings.isError ||
    isErrorRegioner ||
    isErrorAvdelinger ||
    isErrorSeksjoner ||
    isErrorTeams
  const errorDetails = [
    settings.isError && 'innstillinger',
    isErrorRegioner && 'regioner',
    isErrorAvdelinger && 'avdelinger',
    isErrorSeksjoner && 'seksjoner',
    isErrorTeams && 'team'
  ]
    .filter(Boolean)
    .join(', ')
  const error =
    regionerError ??
    avdelingerError ??
    seksjonerError ??
    teamsError ??
    (isError
      ? new Error(`Kunne ikke hente: ${errorDetails || 'data'}.`)
      : undefined)
  return (
    <AppLayout
      sidebar={sidebar}
      topBar={topBar}
      useMobileBar={useMobileBar}
      isLoading={isLoading && !isError}
      isError={isError}
      error={error}
    >
      <ScreenHeader
        title='Velg tilhørighet'
        description={
          <p>
            Her velger du hvilken avdeling/seksjon oppgaver og prøver vises for.
          </p>
        }
      />
      <DivisionSuggestionField
        label='Region / Divisjon'
        placeholder='Ingen region valgt'
        options={regionOptions}
        selectedValue={region}
        onSelectedChange={(value) => {
          setHasLocalChanges(true)
          setRegion(value)
          setAvdeling(undefined)
          setSeksjon(undefined)
          setTeam(undefined)
        }}
      />
      <DivisionSuggestionField
        key={region ?? 'no-region'}
        label='Avdeling'
        placeholder={region ? 'Ingen avdeling valgt' : 'Velg region først'}
        options={avdelingOptions}
        selectedValue={avdeling}
        onSelectedChange={(value) => {
          setHasLocalChanges(true)
          setAvdeling(value)
          setSeksjon(undefined)
          setTeam(undefined)
        }}
      />
      <DivisionSuggestionField
        key={avdeling ?? 'no-avdeling'}
        label='Seksjon'
        placeholder={avdeling ? 'Ingen seksjon valgt' : 'Velg avdeling først'}
        options={seksjonOptions}
        selectedValue={seksjon}
        onSelectedChange={(value) => {
          setHasLocalChanges(true)
          setSeksjon(value)
          setTeam(undefined)
        }}
      />
      <DivisionSuggestionField
        key={`${avdeling ?? 'no-avdeling'}-${seksjon ?? 'no-seksjon'}`}
        label='Team'
        placeholder={
          seksjon || avdeling
            ? 'Ingen team valgt'
            : 'Velg avdeling eller seksjon først'
        }
        options={teamOptions}
        selectedValue={team}
        onSelectedChange={(value) => {
          setHasLocalChanges(true)
          setTeam(value)
        }}
      />
      <Mtds.Divider />
      <Mtds.Flex>
        <Mtds.Button data-arrow='left' onClick={goBack}>
          Avbryt
        </Mtds.Button>
        <Mtds.Button
          onClick={() => {
            setRegion(defaultSettings?.selectedRegionId)
            setAvdeling(defaultSettings?.selectedAvdelingId)
            setSeksjon(defaultSettings?.selectedSeksjonId)
            setTeam(defaultSettings?.selectedTeamId)
            setHasLocalChanges(false)
            set({
              selectedRegionId: defaultSettings?.selectedRegionId,
              selectedAvdelingId: defaultSettings?.selectedAvdelingId,
              selectedSeksjonId: defaultSettings?.selectedSeksjonId,
              selectedTeamId: defaultSettings?.selectedTeamId
            })
            goBack()
          }}
        >
          Tilbakestill
        </Mtds.Button>
        <Mtds.Button
          data-variant='primary'
          onClick={() => {
            if (!region || !avdeling) return alert('Du må velge en avdeling')
            set({
              selectedRegionId: region,
              selectedAvdelingId: avdeling,
              selectedSeksjonId: seksjon,
              selectedTeamId: team
            })
            goBack()
          }}
        >
          <CheckIcon />
          Lagre
        </Mtds.Button>
      </Mtds.Flex>
    </AppLayout>
  )
}
